import React, { createContext, memo, useCallback, useContext, useRef, useState } from 'react';

import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';

const ConfirmationModalContext = createContext();

export const useConfirmationModalContext = (defaultPayload = {}) => {
    const confirm = useContext(ConfirmationModalContext);

    if (!confirm) {
        throw new Error('useConfirmationModal must be used within a ConfirmationModalContext');
    }

    return useCallback(
        (args = defaultPayload) => {
            if (typeof args === 'function') {
                args = {
                    ...defaultPayload,
                    onConfirm: args,
                };
            }

            return confirm({
                ...defaultPayload,
                ...args,
            });
        },
        [confirm, defaultPayload],
    );
};

/**
 * `modalIndexClass` lifts the modal above overlays a page stacks higher than
 * the default, such as a slide-over panel.
 */
export const ConfirmationModalProvider = ({ children, modalIndexClass = 'tw-z-60' }) => {
    const [modalState, setModalState] = useState({
        title: '',
        message: '',
        warning: '',
        actions: [],
        onClose: () => {},
    });

    const [pending, setPending] = useState(false);
    const pendingRef = useRef(false);

    const modal = useModal();

    const setActionPending = useCallback((value) => {
        pendingRef.current = value;
        setPending(value);
    }, []);

    const confirm = useCallback(
        ({ title, message, warning, actions, onConfirm, onCancel, onClose }) => {
            return new Promise((resolve) => {
                setActionPending(false);
                setModalState({
                    title,
                    message,
                    warning,
                    actions: actions || [
                        {
                            confirmed: false,
                            label: $t('Cancel'),
                            variant: 'secondary',
                            onClick: onCancel || (() => {}),
                        },
                        {
                            confirmed: true,
                            label: $t('Confirm'),
                            variant: 'primary',
                            onClick: onConfirm || (() => {}),
                        },
                    ],
                    onClose: () => {
                        onClose && onClose();
                        resolve(null);
                    },
                });

                modal.show();

                const handleActionClick = async (action) => {
                    if (pendingRef.current) {
                        return;
                    }

                    setActionPending(true);

                    try {
                        if (typeof action.onClick === 'function') {
                            await action.onClick();
                        }
                    } catch (error) {
                        setActionPending(false);
                        throw error;
                    }

                    setActionPending(false);
                    resolve(action);
                    modal.hide();
                };

                setModalState((prevState) => ({
                    ...prevState,
                    onActionClick: handleActionClick,
                }));
            });
        },
        [modal, setActionPending],
    );

    const handleClose = useCallback(() => {
        modalState.onClose();
        modal.hide();
    }, [modal, modalState]);

    return (
        <ConfirmationModalContext.Provider value={confirm}>
            <ConfirmationModal modal={modal} modalIndexClass={modalIndexClass} pending={pending} {...modalState} onClose={handleClose} />
            {children}
        </ConfirmationModalContext.Provider>
    );
};

const ConfirmationModal = memo(function ConfirmationModal({ modal, modalIndexClass, title, message, warning, actions, pending, onActionClick, onClose }) {
    return (
        <Modal modal={modal} modalIndexClass={modalIndexClass}>
            <ModalHeader title={title} close={onClose}></ModalHeader>
            <div className="tw-space-y-4">
                <div className="modal__form-container">
                    <div className="modal__msg modal__text-color tw-space-y-1">
                        <div>{message}</div>
                        {warning && (
                            <div className="tw-text-sm tw-font-normal modal__text-color">
                                (<span className="tw-text-red-500">{$t('Warning')}</span> - {warning})
                            </div>
                        )}
                    </div>
                </div>
                <div className="modal__footer">
                    {actions.map((action, index) => (
                        <Button key={index} variant={action.variant} disabled={pending} onClick={() => onActionClick(action)}>
                            {$t(action.label)}
                        </Button>
                    ))}
                </div>
            </div>
        </Modal>
    );
});

export default ConfirmationModalContext;
